import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize, retryWhen, mergeMap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {Cookie} from 'ng2-cookies';
import {Constant} from '../constants/constant.class';
import {LoaderService} from '../../service/loader.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private totalRequests = 0;

  // Cờ chống gọi navigate() nhiều lần cùng lúc. Khi license hết hạn (403) hoặc hết
  // phiên đăng nhập (401), TẤT CẢ request đang chạy song song (validate, load config,
  // load danh sách...) đều trả lỗi cùng lúc -> nếu mỗi lỗi đều tự gọi router.navigate()
  // độc lập thì Router phải xử lý N lần điều hướng gần như đồng thời, gây giật/nhấp nháy
  // màn hình (mỗi lần NavigationEnd lại kích hoạt lại scrollTo, splash-screen... ở
  // app.component.ts). Chỉ cho phép điều hướng 1 lần, các lỗi cùng loại xảy ra sau đó
  // trong lúc đang điều hướng sẽ bị bỏ qua.
  private isRedirectingToLogin = false;
  private isRedirectingExpired = false;

  constructor(private router: Router, private loaderService: LoaderService) {
  }
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!req.headers.get('skipLoading')) {
      this.totalRequests++;
      this.loaderService.show();
    }
    return next.handle(req).pipe(
      // Không retry đối với lỗi xác thực/phân quyền (401/403) vì gọi lại cũng chắc chắn
      // lỗi y hệt -> chỉ làm tăng gấp đôi số request và số lần trigger điều hướng.
      retryWhen(errors => errors.pipe(
        mergeMap((error: HttpErrorResponse, index) => {
          if (index >= 1 || error.status === 401 || error.status === 403) {
            return throwError(error);
          }
          return [error];
        })
      )),

      catchError((error: HttpErrorResponse) => {
        // Các request "nền", không quan trọng bằng điều hướng đang có (vd: xoá FCM
        // token lúc logout) có thể đánh dấu header này để lỗi không kích hoạt điều
        // hướng cả app đi nơi khác, tránh ghi đè mất điều hướng đang thực hiện (vd:
        // logout() vừa điều hướng về trang login, xong ngay sau đó API dọn dẹp lỗi
        // 404 lại điều hướng đè sang /page-not-found).
        if (req.headers.get('skipErrorRedirect')) {
          return throwError(error);
        }

        if (error.status === 401) {
          localStorage.removeItem(Constant.TOKEN);
          localStorage.removeItem(Constant.USER_INFO);
          if (!this.isRedirectingToLogin) {
            this.isRedirectingToLogin = true;
            const url = this.router.routerState.snapshot.url;
            this.router.navigate([Constant.LOGIN], { queryParams: { returnUrl: url } })
              .finally(() => {
                setTimeout(() => this.isRedirectingToLogin = false, 1000);
              });
          }
          return throwError(error);
        } else if (error.status === 403) {
          // Lỗi 403 hiện tại BE dùng riêng cho "hết hạn license/dùng thử" (kèm theo
          // data: {IsValid, Message, RemainDay, DateExpired}). Trước đây chỉ điều hướng
          // sang /admin-login mà KHÔNG xoá token/cookie -> LoginAdminComponent.ngOnInit()
          // thấy cookie token vẫn còn nên tự động navigate ngược lại vào dashboard, dashboard
          // gọi API lại bị 403 tiếp -> tạo vòng lặp ping-pong admin-login <-> dashboard,
          // đây chính là hiện tượng "giật giật" người dùng gặp phải.
          // Fix: xoá sạch token (localStorage + cookie) như logout, lưu tạm nội dung lỗi
          // license vào localStorage rồi điều hướng sang trang thông báo riêng
          // /auth/license-expired thay vì quay lại trang login.
          if (!this.isRedirectingExpired) {
            this.isRedirectingExpired = true;
            localStorage.removeItem(Constant.TOKEN);
            localStorage.removeItem(Constant.USER_INFO);
            Cookie.delete(Constant.TOKEN);

            try {
              const body: any = error.error || {};
              const licenseInfo = {
                message: body.message,
                detail: body.data ? body.data.Message : null,
                remainDay: body.data ? body.data.RemainDay : null,
                dateExpired: body.data ? body.data.DateExpired : null,
              };
              localStorage.setItem(Constant.LICENSE_INFO, JSON.stringify(licenseInfo));
            } catch (e) {
              // ignore, trang license-expired sẽ hiện message mặc định
            }

            this.router.navigate(['/' + Constant.LICENSE_EXPIRED_ROUTE])
              .finally(() => {
                setTimeout(() => this.isRedirectingExpired = false, 1000);
              });
          }
          return throwError(error);
        } else if (error.status === 404) {
          this.router.navigate(['/page-not-found']);
        }
        return throwError(error);
      }),
       finalize(() => {
        if (!req.headers.get('skipLoading')) {
          this.totalRequests--;
        }
        if (this.totalRequests === 0) {
          this.loaderService.hide();
        }
      })
    );
  }
}
